import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Play, Pause, Copy, User, Headphones } from 'lucide-react';
import { api } from '@/api/api-client';
import { toast } from 'sonner';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';

interface CallShape {
  id?: string;
  external_call_id?: string | null;
  recording_url?: string | null;
  prospect_name?: string | null;
  duration_seconds?: number | null;
  call_outcome?: string | null;
  transcription?: string | TranscriptionSegment[] | { text?: string; segments?: TranscriptionSegment[] } | null;
}

interface TranscriptionSegment {
  speaker?: string;
  text?: string;
  channel?: number;
  timestamp?: string;
}

interface ApiShape {
  entities: {
    ParallelCall: { update(id: string, patch: Record<string, unknown>): Promise<unknown> };
  };
  functions: {
    invoke: (
      name: string,
      params: Record<string, unknown>,
    ) => Promise<{ data?: { transcription?: TranscriptionSegment[] | string; recordingUrl?: string } }>;
  };
}

interface CallTranscriptionProps {
  call?: CallShape | null;
}

export default function CallTranscription({ call }: CallTranscriptionProps) {
  const { t } = useTranslation(['admin', 'dialer']);
  const [transcription, setTranscription] = useState<CallShape['transcription'] | null>(call?.transcription || null);
  const [loading, setLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioRef] = useState<HTMLAudioElement | null>(() => (typeof Audio !== 'undefined' ? new Audio() : null));
  const [autoTriggered, setAutoTriggered] = useState(false);

  const handleTranscribe = async () => {
    if (!call?.id || loading) return;
    setLoading(true);
    try {
      const { data } = await (api as unknown as ApiShape).functions.invoke('transcribeCall', {
        callId: call.id,
        callSid: call.external_call_id,
      });
      if (data?.transcription) {
        setTranscription(data.transcription);
        await (api as unknown as ApiShape).entities.ParallelCall.update(call.id, {
          transcription: JSON.stringify(data.transcription),
        });
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      !autoTriggered &&
      !transcription &&
      call?.external_call_id &&
      (call?.duration_seconds ?? 0) > 0
    ) {
      setAutoTriggered(true);
      handleTranscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.id]);

  const handlePlayRecording = async () => {
    if (!call?.recording_url && !call?.external_call_id) {
      toast.error(t('admin:components.callTranscription.noRecording'));
      return;
    }
    try {
      if (audioPlaying) {
        audioRef?.pause();
        setAudioPlaying(false);
        return;
      }
      let url: string | undefined = call.recording_url ?? undefined;
      if (url) {
        const doubled = 'https://api.twilio.comhttps://api.twilio.com';
        if (url.startsWith(doubled)) url = url.replace(doubled, 'https://api.twilio.com');
        url = `/api/twilio/recording-proxy?url=${encodeURIComponent(url)}`;
      } else {
        const { data } = await (api as unknown as ApiShape).functions.invoke('getCallRecording', {
          callId: call.id,
          callSid: call.external_call_id,
        });
        url = data?.recordingUrl;
      }
      if (url && audioRef) {
        audioRef.src = url;
        audioRef.play();
        setAudioPlaying(true);
        audioRef.onended = () => setAudioPlaying(false);
      } else {
        toast.error(t('admin:components.callTranscription.recordingUnavailable'));
      }
    } catch (err) {
      toast.error(t('admin:components.callTranscription.playbackError', { message: (err as Error).message || '' }));
    }
  };

  const segments = useMemo<TranscriptionSegment[] | null>(() => {
    if (!transcription) return null;
    try {
      const parsed = typeof transcription === 'string' ? JSON.parse(transcription) : transcription;
      if (Array.isArray(parsed)) return parsed as TranscriptionSegment[];
      if (parsed.segments) return parsed.segments as TranscriptionSegment[];
      if (parsed.text) return [{ speaker: 'Agent', text: parsed.text }];
    } catch {
      /* fall through */
    }
    if (typeof transcription === 'string') return [{ speaker: 'Agent', text: transcription }];
    return null;
  }, [transcription]);

  const handleCopyTranscript = () => {
    if (!segments) return;
    const text = segments.map((s) => `[${s.speaker || 'Unknown'}]: ${s.text}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success(t('admin:components.callTranscription.transcriptCopied'));
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between surface-1 rounded-lg p-3 border border-border">
        <div className="flex items-center gap-3">
          <div className="text-sm">
            <span className="font-medium text-foreground">{call?.prospect_name}</span>
            {(call?.duration_seconds ?? 0) > 0 && (
              <span className="text-muted-foreground ml-2">({formatDuration(call?.duration_seconds)})</span>
            )}
          </div>
          {call?.call_outcome && (
            <Badge variant="secondary" className="text-xs">
              {outcomeLabel(call.call_outcome, t)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={handlePlayRecording}
            disabled={!call?.external_call_id}
          >
            {audioPlaying ? <Pause className="w-3 h-3 mr-1" /> : <Play className="w-3 h-3 mr-1" />}
            {audioPlaying ? t('admin:components.callTranscription.pause') : t('admin:components.callTranscription.listen')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 bg-card border border-border rounded-lg">
          <Loader2 className="w-8 h-8 text-[var(--vf-accent-text)] mx-auto mb-2 animate-spin" />
          <p className="text-sm text-muted-foreground">{t('admin:components.callTranscription.loading')}</p>
        </div>
      ) : segments ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground/80">
              {t('admin:components.callTranscription.title')}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={handleCopyTranscript}
            >
              <Copy className="w-3 h-3 mr-1" /> {t('admin:components.callTranscription.copy')}
            </Button>
          </div>
          <div className="bg-card border border-border rounded-lg max-h-[400px] overflow-y-auto divide-y divide-border/50">
            {segments.map((segment, idx) => {
              const isAgent =
                (segment.speaker || '').toLowerCase().includes('agent') || segment.channel === 0;
              return (
                <div key={idx} className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        isAgent ? 'bg-[var(--vf-accent-bg)]' : 'bg-green-100'
                      }`}
                    >
                      {isAgent ? (
                        <Headphones className="w-3 h-3 text-[var(--vf-accent-text)]" />
                      ) : (
                        <User className="w-3 h-3 text-green-600" />
                      )}
                    </div>
                    <span
                      className={`text-xs font-semibold ${
                        isAgent ? 'text-[var(--vf-accent-text)]' : 'text-green-600'
                      }`}
                    >
                      {segment.speaker || (isAgent ? t('admin:components.callTranscription.agent') : t('admin:components.callTranscription.prospect'))}
                    </span>
                    {segment.timestamp && (
                      <span className="text-[10px] text-muted-foreground/60 font-mono">{segment.timestamp}</span>
                    )}
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed pl-7">{segment.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-6 bg-card border border-border rounded-lg">
          <FileText className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('admin:components.callTranscription.noTranscript')}</p>
        </div>
      )}
    </div>
  );
}
